import classes from './Navbar.module.scss';
import { Select } from '../../../../components/forms/Select';
import { AnalyticsVisualizationType } from '../AnalyticsDashboard.controller';
import classNames from 'classnames';
import { CalendarPicker } from '../../../../components/actions/CalendarPicker';
import React, { useContext } from 'react';
import { UseFormReturn, useFormState } from 'react-hook-form';
import { useSelectedOrganization } from '../../../../hooks/useSelectedOrganization';
import { DateFormatSetting, StartOfWeekFormatSetting } from '../../../../models/graphql';
import { convertDateFormatSettingToString } from '../../../../helpers/timeFormatter';
import { subYears } from 'date-fns';
import { AnalyticsContext } from '../../AnalyticsProvider';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import {
  SelectItem,
  SelectItemTemplate,
  SelectValueTemplate,
  SelectViewProps
} from '../../../../components/forms/Select/Select.view';
import { SelectProfileTemplateViewProps } from '../../../Discover/FindContent/SelectTemplate/SelectTemplate.view';
import { SelectProfileItemTemplate } from '../../../Discover/FindContent/SelectTemplate';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { profilesHelpers } from '../../../Settings/Profiles/Profiles.helpers';
import { SocialProfileSelector } from '../../../../components/socialProfiles/SocialProfileSelector';
import { Link } from 'react-router-dom';
import { InputErrorMessageSeveritySocialProfileSelectorType } from '../../../../components/socialProfiles/SocialProfileSelector/SocialProfileSelector.view';
import { Tooltip } from 'components/actions/Tooltip';
import { AdvancedFeature } from 'components/containers/AdvancedFeature';
import { AdvancedFeatures } from 'components/containers/AdvancedFeature/AdvancedFeature.view';
import { AnalyticsNavbarState } from './Navbar.reducer';

export type NavbarViewProps = {
  formInstance: UseFormReturn;
  shouldShowSidebar: boolean;
  applyFilterFunction: VoidFunction;
  navbarState: AnalyticsNavbarState;
  isViewOverview: boolean;
  validateProfile: VoidFunction;
  overOneMonthNotAllowed: boolean;
};

export const NavBarView = ({
  formInstance,
  shouldShowSidebar,
  applyFilterFunction,
  isViewOverview,
  navbarState,
  validateProfile,
  overOneMonthNotAllowed
}: NavbarViewProps) => {
  const { startDate, endDate, socialProfiles, view } = useContext(AnalyticsContext);
  const { selectedOrganization } = useSelectedOrganization();
  const rawDate = selectedOrganization?.myUserAssignment?.settings.dateFormat ?? DateFormatSetting.DAY_MONTH_YEAR;
  const startOfDay =
    selectedOrganization?.myUserAssignment?.settings.startOfWeekFormat ?? StartOfWeekFormatSetting.MONDAY;
  const dateFormat = convertDateFormatSettingToString(rawDate);
  const startDateMaxDate = endDate;
  const maxDate = new Date();
  const minDate = subYears(maxDate, 2);
  const formState = useFormState({ control: formInstance.control });
  const { selectedProfilesOverview, profileErrorStateOverview, selectedProfileId, profileErrorState } = navbarState;
  const valueTemplate: SelectValueTemplate = (props: SelectViewProps, globalClasses: string) => {
    const profile = socialProfiles.find(profile => profile.id === props.selectedValue);
    return (
      <div
        className={classNames(globalClasses, classes.SelectValue)}
        onClick={() => {
          props.setExpanded(!props.expanded);
        }}>
        {profile ? (
          <SocialProfile
            socialProfile={{
              id: profile.id,
              provider: profilesHelpers.mapOAuthFromTypename(profile.__typename),
              network: profilesHelpers.mapNetworkFromTypename(profile.__typename),
              name: profile.displayName
            }}
            size={'small'}
            image={profile.photoUrl}
            label={true}
          />
        ) : (
          <span>Select a Profile</span>
        )}
      </div>
    );
  };
  const itemTemplate: SelectItemTemplate = (props: SelectViewProps, index: number, item: SelectItem) => {
    const selectProps: SelectProfileTemplateViewProps = {
      ...props,
      index,
      item,
      showDisplayName: true,
      iconSize: 'medium',
      socialProfiles: socialProfiles
    };

    return <SelectProfileItemTemplate key={index} {...selectProps} />;
  };
  const isOverProfileLimit = selectedProfilesOverview.length >= 13;

  const profileErrorStateOverviewOptions: InputErrorMessageSeveritySocialProfileSelectorType = profileErrorStateOverview
    ? {
        errorSeverity: profileErrorStateOverview.errorSeverity,
        errorMessage: profileErrorStateOverview.message
      }
    : {};

  return (
    <div className={classNames('grid-xy', classes.Navbar)}>
      <div className={'flex-container'}>
        {!shouldShowSidebar && (
          <div className={classes.TypeViewSelector}>
            <Select
              items={[
                {
                  label: 'Overview',
                  value: AnalyticsVisualizationType.overview
                },
                {
                  label: 'Analyze Per Profile',
                  value: AnalyticsVisualizationType.profile
                }
              ]}
              selectedValue={view}
              size={'large'}
              rules={{}}
              useFormInstance={formInstance}
              name={'view'}
            />
          </div>
        )}
        <div className={classNames(classes.SocialProfileContainer)}>
          {isViewOverview ? (
            <SocialProfileSelector
              disabledProfileIds={[]}
              selectItems={socialProfiles}
              selectedProfiles={selectedProfilesOverview}
              useFormInstance={formInstance}
              groupByProfile={true}
              formFieldName={'selectedProfilesOverview'}
              {...profileErrorStateOverviewOptions}
              onClose={validateProfile}
            />
          ) : (
            <Select
              items={socialProfiles.map(profile => ({ label: profile.displayName, value: profile.id }))}
              name={'uniqueProfile'}
              selectedValue={selectedProfileId}
              rules={{ required: false }}
              size={'large'}
              errorSeverity={profileErrorState?.errorSeverity}
              errorMessage={profileErrorState?.message ?? ''}
              useFormInstance={formInstance}
              hideDefaultSelectOption
              itemTemplate={itemTemplate}
              valueTemplate={valueTemplate}
              emptyListItemTemplate={
                <>
                  No Profiles connected.
                  <br className={'show-for-medium'} />
                  <Link to={'/settings/profiles'} target={'_blank'}>
                    Connect a Profile
                  </Link>{' '}
                  to add Posts
                </>
              }
            />
          )}
        </div>
      </div>
      <div className={classNames(classes.AdvancedFeatureContainer)}>
        {overOneMonthNotAllowed && (
          <div className={classes.AdvancedFeature}>
            <AdvancedFeature feature={AdvancedFeatures.analyticsFilter} />
          </div>
        )}
        <div className={classNames('flex-container', classes.DatesContainer)}>
          <div className={classes.CalendarPicker}>
            <CalendarPicker
              key={isViewOverview ? 'datepicker-overview' : 'datepicker-analyse'} // we need this because react does not unmount the component if its the same component with different props
              name={'startRange'}
              useFormInstance={formInstance}
              inputSize={'large'}
              rules={{}}
              date={startDate}
              startDay={startOfDay}
              calendarProps={{ dateFormat, minDate, maxDate: startDateMaxDate }}
            />
          </div>
          <div
            className={classNames(
              `${formState.errors['startRange'] && 'margin-bottom-small-16'} flex-container align-middle`,
              {
                [classes.DashWithError]: profileErrorStateOverview
              }
            )}>
            <span className={classes.Dash}>-</span>
          </div>
          <div className={classes.CalendarPicker}>
            <CalendarPicker
              key={isViewOverview ? 'datepicker-overview' : 'datepicker-analyse'} // we need this because react does not unmount the component if its the same component with different props
              name={'endRange'}
              useFormInstance={formInstance}
              inputSize={'large'}
              rules={{}}
              startDay={startOfDay}
              date={endDate}
              calendarProps={{ dateFormat, maxDate, minDate }}
            />
          </div>

          <div className={classes.ApplyButton}>
            {isOverProfileLimit && (
              <Tooltip type={'regular'} target={'#applyButton'} position={'left'}>
                You can only view reports for up to 12 profiles at a time. Please select fewer profiles.
              </Tooltip>
            )}
            <div id={'applyButton'}>
              <Button
                action={applyFilterFunction}
                type={'button'}
                variant={'solid'}
                size={'large'}
                color={ButtonColorsEnum.primary}
                disabled={isOverProfileLimit}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
